import React, { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { ArrowRight, ShieldCheck, Lock } from "lucide-react";
import { toast } from "sonner";
import BrandMark from "../components/BrandMark";
import Ticker from "../components/Ticker";
import { useAdminAuth } from "../context/AdminAuthContext";

export default function AdminLoginPage() {
  const { me, login, loading } = useAdminAuth();
  const [email, setEmail] = useState("admin@authoros.dev");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  if (!loading && me) return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await login({ email: email.trim(), password });
      toast.success("Welcome, admin.");
      navigate("/admin");
    } catch (err) {
      setError(err?.response?.data?.detail || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-brand-ink text-brand-ivory">
      <Ticker />
      <div className="mx-auto grid max-w-[1100px] grid-cols-1 gap-8 px-5 py-10 sm:px-8 sm:py-12 md:grid-cols-[1fr_460px] md:gap-10">
        <div className="pt-4 md:pt-8">
          <div className="flex items-center gap-3">
            <BrandMark size={40} />
            <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-brand-mute">
              Admin Console
            </div>
          </div>
          <h1 className="mt-6 font-serif-display text-[42px] font-medium leading-[0.98] tracking-tight sm:mt-8 sm:text-[54px] lg:text-[64px]">
            Studio<br />
            <span className="italic text-brand">control</span>.
          </h1>
          <p className="mt-4 max-w-[440px] text-[14px] leading-relaxed text-brand-ivory/65">
            Manage orders, consult submissions and per-service pricing & imagery. Access is limited to the studio admin.
          </p>
          <div className="mt-8 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.28em] text-brand-mute">
            <ShieldCheck className="h-3.5 w-3.5 text-brand" /> Encrypted session · 12h token
          </div>
        </div>

        <div className="rounded-xl border border-brand-hair bg-brand-panel p-6 sm:p-8">
          <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-brand-mute">
            Sign in
          </div>
          <h2 className="mt-2 font-serif-display text-[30px] font-medium leading-tight">
            Admin login
          </h2>

          <form onSubmit={submit} className="mt-6 space-y-4">
            <label className="block">
              <div className="mb-1.5 font-mono text-[10px] uppercase tracking-[0.28em] text-brand-mute">
                Email
              </div>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full rounded-md border border-brand-hair bg-brand-panel2 px-4 py-3 text-[13.5px] text-brand-ivory focus:border-brand"
              />
            </label>
            <label className="block">
              <div className="mb-1.5 font-mono text-[10px] uppercase tracking-[0.28em] text-brand-mute">
                Password
              </div>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full rounded-md border border-brand-hair bg-brand-panel2 px-4 py-3 text-[13.5px] text-brand-ivory focus:border-brand"
                placeholder="demo123"
              />
            </label>
            {error && (
              <div className="font-mono text-[11px] uppercase tracking-widest text-brand">{error}</div>
            )}
            <button
              type="submit"
              disabled={busy}
              className="group flex w-full items-center justify-center gap-3 rounded-md bg-brand py-3 font-mono text-[12px] font-semibold uppercase tracking-[0.28em] text-white transition-colors hover:bg-[#ff5c26] disabled:opacity-60"
            >
              <Lock className="h-4 w-4" />
              {busy ? "Signing in..." : "Sign in"}
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </button>
            <div className="mt-2 font-mono text-[10px] uppercase tracking-[0.28em] text-brand-mute">
              Demo creds: admin@authoros.dev · demo123
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
